import { APP_MODE_TYPES, appMode } from 'src/config'
import { CONDITIONS, CONDITIONS_REQUIREMENTS, TYPES } from 'src/models/booking-fee-type/BookingFeeType'

/**
 * Computes the highest priority applicable BookingFeeType for a Ticket OrderItem.
 */
class BookingFeesTicketComputer {
  static #instance = null

  constructor () {
    if (!BookingFeesTicketComputer.#instance) {
      BookingFeesTicketComputer.#instance = this
    }
    return BookingFeesTicketComputer.#instance
  }

  /**
   * Compute the highest priority BookingFeeType for the given Ticket OrderItem,
   * based on the OrderItem's state and any conditions configured for each BookingFeeType.
   * @param {OrderItem} orderItem
   * @returns {BookingFeeType} validBookingFeeType
   */
  compute (orderItem) {
    const bookingFeeTypes = _.sortBy(orderItem.configuration?.bookingFeeTypes?.data, 'priorityOrder')

    const validBookingFeeType = _.find(bookingFeeTypes, bookingFeeType => {
      if (bookingFeeType.type !== TYPES.TICKET) return false
      if (!bookingFeeType.isActive) return false

      const channel = this.#channel(orderItem)
      if (!bookingFeeType.channels[channel]) return false

      if (bookingFeeType.conditions[CONDITIONS.PRICE]) {
        const price = orderItem.setAsGross
          ? orderItem.total - orderItem.bookingFeeTotal
          : orderItem.subtotal - orderItem.bookingFeeAmount

        const atLeastMinPrice = price >= bookingFeeType.conditionsRequirements[CONDITIONS_REQUIREMENTS.MIN_PRICE]
        const atMostMaxPrice = _.isNil(bookingFeeType.conditionsRequirements[CONDITIONS_REQUIREMENTS.MAX_PRICE]) ||
                              price <= bookingFeeType.conditionsRequirements[CONDITIONS_REQUIREMENTS.MAX_PRICE]
        const includesPrice = atLeastMinPrice && atMostMaxPrice

        if (bookingFeeType.conditionsRequirements[CONDITIONS_REQUIREMENTS.EXCLUDE_PRICE] === includesPrice) return false
      }

      if (bookingFeeType.conditions[CONDITIONS.MEMBERSHIP_TYPES]) {
        if (bookingFeeType.conditionsRequirements[CONDITIONS_REQUIREMENTS.EXCLUDE_MEMBERSHIP_TYPES] === _.includes(bookingFeeType.membershipTypeIds, orderItem.effectiveUser?.effectiveMembershipType?.id)) return false
      }

      if (bookingFeeType.conditions[CONDITIONS.MEMBERSHIP_TIERS]) {
        if (bookingFeeType.conditionsRequirements[CONDITIONS_REQUIREMENTS.EXCLUDE_MEMBERSHIP_TIERS] === _.includes(bookingFeeType.membershipTierIds, orderItem.effectiveUser?.membershipTier?.id)) return false
      }

      if (bookingFeeType.conditions[CONDITIONS.TICKET_TYPES]) {
        if (bookingFeeType.conditionsRequirements[CONDITIONS_REQUIREMENTS.EXCLUDE_TICKET_TYPES] === _.includes(bookingFeeType.ticketTypeIds, orderItem.ticket?.ticketTypeId)) return false
      }

      if (bookingFeeType.conditions[CONDITIONS.SHOWING_BADGES]) {
        const includesAllShowingBadges = _.difference(bookingFeeType.showingBadgeIds, orderItem.ticket?.showing.showingBadgeIds).length === 0
        if (bookingFeeType.conditionsRequirements[CONDITIONS_REQUIREMENTS.EXCLUDE_SHOWING_BADGES] === includesAllShowingBadges) return false
      }

      return true
    })

    return validBookingFeeType
  }

  // private

  /**
   * Gets the appropriate channel for the given OrderItem.
   * The channel is determined by the being application used to place the given OrderItem.
   * @param {OrderItem} orderItem
   * @returns {String} channel
   */
  #channel (orderItem) {
    let channel

    switch (appMode) {
      case APP_MODE_TYPES.REGISTER:
        channel = orderItem?.order?.phoneOrder ? 'phone' : 'register'
        break
      case APP_MODE_TYPES.CONSUMER:
        channel = 'website'
        break
      default:
        channel = appMode
    }

    return channel
  }
}

const ticketBookingFeeComputer = new BookingFeesTicketComputer()
export default ticketBookingFeeComputer
